import { api } from '../../../servicesGlobais/api'
import { extractErrorMessage } from '../../../utils/formatUtils'

export interface FaturamentoDto {
  idFaturamento: number
  idProjeto: number
  codigoEtapaFaturamento?: number
  nf?: number
  ticketsNf?: unknown[]
  dataEnvioAnalista?: string
  dataEnvioMomentum?: string
  dataAprovacao?: string
  dataFaturamento?: string
  valorTotal: number
  observacao?: string
  motivoReprova?: string
  dataCadastro: string
  projetoNome: string
  projetoNumeroTicket?: string
  analistaSMNId: number
  analistaSMNNome?: string
  statusCodigo?: number
  statusDescricao?: string
  apontamentos?: FaturamentoTimeEntry[]
  totalHoras?: number
  idTime?: number | null
  nomeTime?: string | null
  /** Linhas de suporte por lote de planilha (acompanhamento) */
  idLoteGeracaoSuporte?: number | null
  /** Preenchido no acompanhamento (analista): colaboradores do time com linha em FaturamentoColaboradores */
  colaboradoresResumo?: FaturamentoColaboradorResumo[]
  /** Planilha de suporte: minutos oficiais da quinzena */
  totalMinutosPlanilha?: number | null
  /** Snapshot da taxa horária efetiva no fechamento (auditoria) */
  taxaHoraAplicada?: number | null
  /** Id do registro ValoresHora utilizado no cálculo */
  idValorHoraReferencia?: number | null
}

export interface FaturamentoStatusDto {
  codigo: number
  descricao: string
}

export interface BuscarFaturamentosDto {
  numeroTicket?: string
  nomeProjeto?: string
  analistaSMNIds?: number[]
  dataInicio: string
  dataFim?: string
  statusCodigos?: number[]
  /** Busca parcial pelo número da NF vinculada ao faturamento */
  numeroNotaFiscal?: string
  semNotaFiscal?: boolean
}

export interface BuscarAcompanhamentoFaturamentoDto {
  numeroTicket?: string
  nomeProjeto?: string
  dataInicio: string
  dataFim?: string
  statusCodigo?: number
  analistaSMNId?: number
  idTime?: number
}

export interface FaturamentoTimeEntry {
  idClockifyTimeEntry: number
  idClockify: string
  responsavelNome?: string
  descricao?: string
  dataInicio?: string
  minutos?: number
  horas: number
  tags?: string[]
}

export interface FaturamentoColaboradorResumo {
  pessoaId: number
  nome: string
  totalMinutos: number
  horas: number
  valor: number
}

export interface FaturamentoDetalhesDto {
  idFaturamento: number
  idProjeto: number
  projetoNome: string
  projetoDataRecebimento: string
  analistaSMNId: number
  analistaSMNNome?: string
  tipoProjetoNome: string
  valorHora: number
  valorHoraDataInicioValidade: string
  escopoOrcado?: number
  desenvolvimentoOrcado?: number
  testesOrcado?: number
  homologacaoCobrado?: number
  valorTotalEstimativa: number
  /** Presente na resposta de GET /faturamento/{id}/detalhes (mesmo shape que FaturamentoDto). */
  valorTotal?: number
  solicitanteId?: number
  solicitanteNome?: string
  observacao?: string
  motivoReprova?: string
  dataAprovacao?: string
  apontamentos?: FaturamentoTimeEntry[]
  documentos?: Array<{
    idDocumento: number
    nomeArquivo: string
    extensao: string
    tipoDocumentoCodigo: number
    tipoDocumentoNome: string
    dataCadastro: string
  }>
  colaboradoresResumo?: FaturamentoColaboradorResumo[]
  totalMinutosPlanilha?: number | null
  taxaHoraAplicada?: number | null
  idValorHoraReferencia?: number | null
}

export interface AnalistaSMNDto {
  pessoaId: number
  nome: string
}

export interface EtapaFaturamentoDto {
  codigoEtapaFaturamento: number
  descricao: string
  percentual: number
}

export interface LancamentoManualTicketResumoDto {
  tipo: string
  idTicket: number
  nome: string
  numeroTicket: string
  valorHoraEfetivo: number
  dataRecebimento: string
  rotuloTipoExibicao: string
}

export interface LancamentoManualPreviewDto {
  tipo: string
  idTicket: number
  nome: string
  numeroTicket: string
  valorHoraEfetivo: number
  tipoProjetoCodigo: number
  dataRecebimento: string
  analistaSMNId: number | null
  analistaSMNNome: string | null
  /** Orçamento, Apontamento, etc.; em suporte: "Suporte" */
  rotuloTipoExibicao?: string
}

export interface LancamentoManualColaboradorLinhaDto {
  idPessoa: number
  minutos: number
}

export interface LancamentoManualFaturamentoPayload {
  analistaSMNId: number
  tipoTicket: string
  idTicket: number
  codigoEtapaFaturamento: number
  colaboradores: LancamentoManualColaboradorLinhaDto[]
  dataEnvioAnalista?: string | null
  dataEnvioMomentum?: string | null
  dataAprovacao?: string | null
  dataFaturamento?: string | null
  numeroNf?: string | null
  observacao?: string | null
}

export interface OrcamentoCobradoDto {
  escopoCobrado: number
  desenvolvimentoCobrado: number
  testesCobrado: number
  homologacaoCobrado: number
}

export interface UploadPlanilhaSuportePayload {
  dataEnvioMomentum: string
  dataAprovacao: string
  dataFaturamento: string
  quinzena: number
  arquivo: File
}

export interface PlanilhaSuportePreviewLinhaSemIdDto {
  numeroLinhaPlanilha: number
  trechoIdTicketInformado: string
  numeroNotaFiscal: string
  minutosLinha: number
}

export interface PlanilhaSuportePreviewLinhaProblemaDto {
  idTicket: number
  motivo: string
}

export interface PlanilhaSuportePreviewColaboradorDto {
  pessoaId: number
  nome: string
  minutosBrutosClockify: number
  minutosAposRedistribuicao: number
  valorAlocadoEstimado: number
}

/** Pré-visualização de um apontamento: base (cadastro) e cota da planilha no colaborador. */
export interface PlanilhaSuportePreviewApontamentoDto {
  idClockifyTimeEntry: number
  pessoaId: number | null
  colaboradorNome: string | null
  idTicket: number | null
  numeroTicket: string | null
  minutosBase: number
  minutosPlanilhaCota: number
  descricao: string | null
}

export interface PlanilhaSuportePreviewTicketConferenciaDto {
  idTicket: number
  numeroTicket: string | null
  minutosAtribuidosPlanilha: number
  minutosSomaApontamentosBanco: number
}

export interface PlanilhaSuportePreviewDto {
  totalMinutosPlanilha: number
  totalMinutosApontamentosBrutos: number
  totalMinutosAposRedistribuicao: number
  deltaMinutosBrutosVsPlanilha: number
  taxaHoraAplicada: number
  idValorHoraReferencia: number
  dataInicioValidadeValorHora: string
  dataFimValidadeValorHora?: string | null
  valorTotalProjetado: number
  quantidadeLinhasPlanilha: number
  quantidadeTicketsDistintos: number
  quantidadeApontamentosSelecionados: number
  alertas: string[]
  colaboradores: PlanilhaSuportePreviewColaboradorDto[]
  /** Pode ser omitido em respostas antigas. */
  apontamentos?: PlanilhaSuportePreviewApontamentoDto[]
  /** Minutos do arquivo por id de ticket vs soma no banco, antes da redistribuição por colaborador. */
  conferenciaTickets?: PlanilhaSuportePreviewTicketConferenciaDto[]
  linhasSemIdTicketNumerico: PlanilhaSuportePreviewLinhaSemIdDto[]
  linhasComProblema: PlanilhaSuportePreviewLinhaProblemaDto[]
}

export const faturamentoService = {
  async buscarFaturamentos(filtros: BuscarFaturamentosDto): Promise<FaturamentoDto[]> {
    try {
      const params = new URLSearchParams()
      if (filtros.numeroTicket) params.append('numeroTicket', filtros.numeroTicket)
      if (filtros.nomeProjeto) params.append('nomeProjeto', filtros.nomeProjeto)
      filtros.analistaSMNIds?.forEach(id => params.append('analistaSMNIds', id.toString()))
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio)
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim)
      filtros.statusCodigos?.forEach(c => params.append('statusCodigos', c.toString()))
      if (filtros.numeroNotaFiscal?.trim()) params.append('numeroNotaFiscal', filtros.numeroNotaFiscal.trim())
      if (filtros.semNotaFiscal === true) params.append('semNotaFiscal', 'true')
      
      return await api.get(`/faturamento/buscar?${params.toString()}`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao buscar faturamentos'))
    }
  },

  async buscarAcompanhamento(filtros: BuscarAcompanhamentoFaturamentoDto): Promise<FaturamentoDto[]> {
    const params = new URLSearchParams()
    if (filtros.numeroTicket) params.append('numeroTicket', filtros.numeroTicket)
    if (filtros.nomeProjeto) params.append('nomeProjeto', filtros.nomeProjeto)
    if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio)
    if (filtros.dataFim) params.append('dataFim', filtros.dataFim)
    if (filtros.statusCodigo) params.append('statusCodigo', filtros.statusCodigo.toString())
    if (filtros.analistaSMNId) params.append('analistaSMNId', filtros.analistaSMNId.toString())
    if (filtros.idTime) params.append('idTime', filtros.idTime.toString())

    return await api.get(`/faturamento/acompanhamento?${params.toString()}`)
  },

  async obterStatusFaturamento(): Promise<FaturamentoStatusDto[]> {
    try {
      return await api.get('/faturamento/status')
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao carregar status de faturamento'))
    }
  },

  async obterDetalhes(id: number): Promise<FaturamentoDetalhesDto> {
    try {
      return await api.get(`/faturamento/${id}/detalhes`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao obter detalhes do faturamento'))
    }
  },

  async obterFaturamentosPorProjeto(projetoId: number): Promise<FaturamentoDto[]> {
    try {
      return await api.get(`/faturamento/projeto/${projetoId}`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao obter faturamentos do projeto'))
    }
  },

  async atualizarCamposFaturamento(id: number, dados: {
    nf?: string | null
    dataEnvioMomentum?: string | null
    dataAprovacao?: string | null
    dataFaturamento?: string | null
  }): Promise<void> {
    try {
      const payload: any = {
        idFaturamento: id
      }

      if (dados.nf !== undefined)
        payload.nf = dados.nf
      if (dados.dataEnvioMomentum !== undefined)
        payload.dataEnvioMomentumString = dados.dataEnvioMomentum
      if (dados.dataAprovacao !== undefined)
        payload.dataAprovacaoString = dados.dataAprovacao
      if (dados.dataFaturamento !== undefined)
        payload.dataFaturamentoString = dados.dataFaturamento

      await api.put('/faturamento/atualizar-campos', payload)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao atualizar faturamento'))
    }
  },

  async reprovar(id: number, motivoReprovacao: string): Promise<void> {
    try {
      await api.post('/faturamento/reprovar', {
        idFaturamento: id,
        motivoReprova: motivoReprovacao
      })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao reprovar faturamento'))
    }
  },

  async reprovarAdministrativo(id: number, dataReprovacao: string, motivo: string): Promise<void> {
    await api.post('/faturamento/reprovar-administrativo', {
      idFaturamento: id,
      dataReprovacao,
      motivo
    })
  },

  async aprovar(id: number): Promise<void> {
    try {
      await api.post('/faturamento/aprovar', {
        idFaturamento: id
      })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao aprovar faturamento'))
    }
  },

  async obterAnalistasSMN(): Promise<AnalistaSMNDto[]> {
    try {
      return await api.get('/faturamento/analistas-smn')
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao obter analistas SMN'))
    }
  },

  async downloadDocumentos(id: number): Promise<Blob> {
    try {
      const response = await api.get(`/faturamento/${id}/download-documentos`, {
        responseType: 'blob'
      })
      return response
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao fazer download dos documentos'))
    }
  },

  async uploadDocumento(projetoId: number, tipoDocumentoCodigo: number, arquivo: File, nomeArquivo: string): Promise<void> {
    try {
      const formData = new FormData()
      formData.append('arquivo', arquivo)
      formData.append('tipoDocumentoCodigo', tipoDocumentoCodigo.toString())
      formData.append('nomeArquivo', nomeArquivo)
      
      await api.post(`/documento/projeto/${projetoId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao enviar documento'))
    }
  },

  async downloadDocumento(idDocumento: number): Promise<Blob> {
    try {
      const response = await api.get(`/documento/${idDocumento}/download`, {
        responseType: 'blob'
      })
      return response
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao fazer download do documento'))
    }
  },

  async uploadPlanilhaSuporte(payload: UploadPlanilhaSuportePayload): Promise<FaturamentoDto> {
    const formData = new FormData()
    formData.append('arquivo', payload.arquivo)
    formData.append('dataEnvioMomentum', payload.dataEnvioMomentum)
    formData.append('dataAprovacao', payload.dataAprovacao)
    formData.append('dataFaturamento', payload.dataFaturamento)
    formData.append('quinzena', payload.quinzena.toString())

    try {
      const resultado = await api.post('/faturamento/suporte/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return resultado
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao processar planilha de suporte'))
    }
  },

  async previewPlanilhaSuporte(payload: UploadPlanilhaSuportePayload): Promise<PlanilhaSuportePreviewDto> {
    const formData = new FormData()
    formData.append('arquivo', payload.arquivo)
    formData.append('dataEnvioMomentum', payload.dataEnvioMomentum)
    formData.append('dataAprovacao', payload.dataAprovacao)
    formData.append('dataFaturamento', payload.dataFaturamento)
    formData.append('quinzena', payload.quinzena.toString())

    try {
      return await api.post('/faturamento/suporte/planilha/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao pré-visualizar planilha de suporte'))
    }
  },

  async obterEtapasFaturamento(): Promise<EtapaFaturamentoDto[]> {
    try {
      return await api.get('/faturamento/etapas')
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao obter etapas de faturamento'))
    }
  },

  async obterOrcamentoCobrado(projetoId: number): Promise<OrcamentoCobradoDto> {
    try {
      return await api.get(`/faturamento/orcamento-cobrado/${projetoId}`)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao obter orçamento cobrado'))
    }
  },

  async listarTicketsLancamentoManual(analistaSMNId: number, search?: string): Promise<LancamentoManualTicketResumoDto[]> {
    const params = new URLSearchParams()
    params.append('analistaSMNId', String(analistaSMNId))
    if (search?.trim())
      params.append('search', search.trim())
    return await api.get(`/faturamento/lancamento-manual/tickets?${params.toString()}`)
  },

  async obterPreviewLancamentoManual(
    analistaSMNId: number,
    tipoTicket: string,
    idTicket: number
  ): Promise<LancamentoManualPreviewDto> {
    const params = new URLSearchParams()
    params.append('analistaSMNId', String(analistaSMNId))
    params.append('tipoTicket', tipoTicket)
    params.append('idTicket', String(idTicket))
    return await api.get(`/faturamento/lancamento-manual/preview?${params.toString()}`)
  },

  async lancarManualFaturamento(payload: LancamentoManualFaturamentoPayload): Promise<FaturamentoDto> {
    try {
      return await api.post('/faturamento/lancamento-manual', payload)
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Erro ao registrar lançamento manual'))
    }
  },
}

